// ── AR Flow — NOVA INTERFACE: Tombamento da Prospecção (tela) ────────────────
// Busca a fila do dia no servidor (/api/prospeccao/fila), pede a justificativa
// do que ficou pendente e mostra o placar para o gerente. A fila em si é
// montada e gravada SÓ pelo servidor.

import { useCallback, useEffect, useRef, useState } from "react";
import { watchProspRefresh } from "./prospRefresh";
import { createOwnActionSnapshots } from "../ownActionSnapshots.mjs";
import { closingDiagnostic, diagnosticMark, diagnosticHeaders, observeListFrame } from "../saveDiagnostics.mjs";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";

export function filaOperatorContext({ loggedInUser, currentUser }) {
  const operator = loggedInUser || currentUser;
  const role = operator?.role;
  const management = ["manager", "admin", "superadmin"].includes(role);
  const viewingConsultant = management
    && ["admin", "superadmin"].includes(role)
    && currentUser?.role === "consultant";
  const isAuthenticatedConsultant = role === "consultant";
  const matchesAuthenticatedConsultant = isAuthenticatedConsultant
    && currentUser?.role === "consultant"
    && Number(currentUser.id) === Number(operator.id);
  return {
    operator,
    role,
    management,
    viewingConsultant,
    consultantId: viewingConsultant ? currentUser.id : null,
    active: isAuthenticatedConsultant || viewingConsultant,
    readOnly: !matchesAuthenticatedConsultant,
    scopeKey: JSON.stringify([
      operator?.id ?? null,
      role ?? null,
      operator?.storeId ?? null,
      viewingConsultant ? currentUser?.id ?? null : null,
    ]),
  };
}

// Some Meu Dia cards read directly from authenticated CRM endpoints. Keep their
// component trees unmounted during a presentation, even if a real operator's
// session cookie is still present in the browser.
export function ProspFilaDemoBoundary({ demo = false, fallback = null, children }) {
  return demo ? fallback : children;
}

export function useProspFila(ativo, consultantId = null, { scopeKey = "" } = {}) {
  const chave = JSON.stringify([
    Boolean(ativo),
    scopeKey,
    consultantId != null && consultantId !== "" ? String(consultantId) : null,
  ]);
  const [estado, setEstado] = useState({ carregando: false, erro: "", fila: null, chave: null });
  const ownDone = useRef(createOwnActionSnapshots());
  const vivo = useRef(true);
  const chaveAtual = useRef(chave);
  const requisicaoAtual = useRef(0);
  const chaveAnterior = useRef(chave);
  if (chaveAnterior.current !== chave) {
    ownDone.current.clear();
    chaveAnterior.current = chave;
    requisicaoAtual.current += 1;
  }
  chaveAtual.current = chave;
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; requisicaoAtual.current += 1; };
  }, []);
  const recarregar = useCallback(async () => {
    if (!ativo || chaveAtual.current !== chave) return;
    const diagnostic = closingDiagnostic();
    const chavePedido = chave;
    const idPedido = ++requisicaoAtual.current;
    const snapshotRead = ownDone.current.startRead();
    setEstado(previous => ({
      carregando: true,
      erro: "",
      fila: previous.chave === chavePedido ? previous.fila : null,
      chave: chavePedido,
    }));
    try {
      const query = consultantId != null && consultantId !== ""
        ? `?consultantId=${encodeURIComponent(consultantId)}`
        : "";
      diagnosticMark(diagnostic, "fila_request_start");
      const r = await fetch(`/api/prospeccao/fila${query}`, {
        credentials: "same-origin", cache: "no-store",
        ...(diagnostic ? { headers:diagnosticHeaders(diagnostic) } : {}),
      });
      diagnosticMark(diagnostic, "fila_response_headers", { status:r.status });
      const j = await r.json().catch(() => ({}));
      diagnosticMark(diagnostic, "fila_response_body", { status:r.status });
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (Array.isArray(j.itens)) {
        j.itens = ownDone.current.reconcile(`${chavePedido}:${j.data}`,j.itens.map(item =>
          ({...item,id:item.telefone || item.phone || item.id})),snapshotRead);
        j.feitos = j.itens.filter(item => item.status === "feito").length;
      }
      if (vivo.current && chaveAtual.current === chavePedido && requisicaoAtual.current === idPedido) {
        setEstado({ carregando: false, erro: "", fila: j, chave: chavePedido });
        diagnosticMark(diagnostic, "fila_request_end", { outcome:"ok" });
        observeListFrame(diagnostic, "list_ready");
      } else {
        diagnosticMark(diagnostic, "fila_request_end", { outcome:"discarded" });
      }
    } catch (e) {
      diagnosticMark(diagnostic, "fila_request_end", { outcome:"error" });
      if (vivo.current && chaveAtual.current === chavePedido && requisicaoAtual.current === idPedido) {
        setEstado({ carregando: false, erro: e.message || "Falha ao carregar a fila.", fila: null, chave: chavePedido });
      }
    }
  }, [ativo, chave, consultantId]);
  useEffect(() => {
    if (!ativo) {
      setEstado({ carregando: false, erro: "", fila: null, chave });
      return undefined;
    }
    return watchProspRefresh(recarregar);
  }, [ativo, chave, consultantId, recarregar]);
  const correspondeAoContexto = estado.chave === chave;
  return {
    carregando: correspondeAoContexto ? estado.carregando : Boolean(ativo),
    erro: correspondeAoContexto ? estado.erro : "",
    fila: correspondeAoContexto ? estado.fila : null,
    participa: correspondeAoContexto ? estado.fila?.participa : undefined,
    recarregar,
    confirmarFeito(proof) {
      if (proof?.ok !== true || proof.item?.status !== "feito") throw new Error("O servidor não confirmou o Feito.");
      const saved = {...proof.item,id:proof.item.telefone || proof.item.phone};
      ownDone.current.remember(`${chave}:${proof.data}`,saved,{});
      setEstado(previous => previous.chave !== chave || previous.fila?.data !== proof.data ? previous : {
        ...previous,fila:{...previous.fila,itens:previous.fila.itens.map(item =>
          (item.telefone || item.phone) === saved.id ? {...item,...proof.item} : item),
          feitos:previous.fila.itens.filter(item => item.status === "feito"
            || (item.telefone || item.phone) === saved.id).length},
      });
    },
  };
}

export function useProspPlacar(ativo) {
  const [estado, setEstado] = useState({ erro: "", placar: null });
  useEffect(() => {
    if (!ativo) return undefined;
    let vivo = true;
    const carregar = async () => {
      try {
        const r = await fetch("/api/prospeccao/placar", { credentials: "same-origin", cache: "no-store" });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (vivo) setEstado({ erro: "", placar: j });
      } catch (e) { if (vivo) setEstado(s => ({ ...s, erro: e.message })); }
    };
    const parar = watchProspRefresh(carregar);
    return () => { vivo = false; parar(); };
  }, [ativo]);
  return estado;
}

// Meu Dia também prepara as filas ao ser aberto por gerente/admin, sem
// consultar uma fila individual nem depender de dados gerais do aplicativo.
export function useProspDiaPreparado(ativo, { scopeKey = "" } = {}) {
  const chave = JSON.stringify([Boolean(ativo), scopeKey]);
  const [estado, setEstado] = useState({ chave: null, carregando: false, erro: "", preparado: false });
  const chaveAtual = useRef(chave);
  chaveAtual.current = chave;
  useEffect(() => {
    if (!ativo) {
      setEstado({ chave, carregando: false, erro: "", preparado: false });
      return undefined;
    }
    let vivo = true;
    let emAndamento = false;
    const controller = new AbortController();
    const carregar = async () => {
      if (emAndamento || !vivo) return;
      emAndamento = true;
      setEstado(s => ({ ...s, chave, carregando: true, erro: "" }));
      try {
        const r = await fetch("/api/meu-dia/prospeccao/status", {
          credentials: "same-origin", cache: "no-store", signal: controller.signal,
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (typeof j.preparado !== "boolean" || typeof j.data !== "string") {
          throw new Error("O servidor não confirmou a preparação da prospecção.");
        }
        if (vivo && chaveAtual.current === chave) {
          setEstado({ chave, carregando: false, erro: "", preparado: j.preparado });
        }
      } catch (e) {
        if (vivo && chaveAtual.current === chave) {
          setEstado({ chave, carregando: false, erro: e.message || "Não foi possível preparar a prospecção.", preparado: false });
        }
      } finally { emAndamento = false; }
    };
    const parar = watchProspRefresh(carregar);
    return () => { vivo = false; controller.abort(); parar(); };
  }, [ativo, chave]);
  return estado.chave === chave ? estado : { carregando: Boolean(ativo), erro: "", preparado: false };
}

export function JustificativaTombamento({ theme, justificarDe, motivos, onSalvo, readOnly = false }) {
  const th = theme;
  const [respostas, setRespostas] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const daily = justificarDe?.modo === "dia";
  const identity = JSON.stringify([justificarDe?.data, justificarDe?.storeId, justificarDe?.consultantId, daily]);
  useEffect(() => { setRespostas({}); setErro(""); }, [identity]);
  if (readOnly) return null;
  if (!(daily ? justificarDe?.pendentes : justificarDe?.itens?.length)) return null;
  const itens = daily ? [{ idx:"dia", nome:"Motivo do dia" }] : justificarDe.itens;
  const completo = itens.every(it => respostas[it.idx]?.motivo && (respostas[it.idx].motivo !== "outro" || respostas[it.idx].texto?.trim()));
  const salvar = async () => {
    if (!completo || salvando) return;
    setSalvando(true); setErro("");
    try {
      const r = await fetch(daily ? "/api/prospeccao/fila/justificar-dia" : "/api/prospeccao/fila/justificar", {
        method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(daily ? { data:justificarDe.data, ...respostas.dia }
          : { data: justificarDe.data, justificativas: itens.map(it => ({ idx: it.idx, ...respostas[it.idx] })) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      onSalvo?.();
    } catch (e) { setErro(e.message || "Não foi possível salvar."); }
    finally { setSalvando(false); }
  };
  const [, m, d] = String(justificarDe.data).split("-");
  return (
    <div role="dialog" aria-modal="true" aria-label="Justificar contatos pendentes" style={{
      position: "fixed", inset: 0, zIndex: 300, background: "rgba(15,31,61,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 8, padding: 22, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: th.text }}>{daily
          ? "Por que não fez a prospecção de ontem?"
          : `Antes de começar: prospecção de ${d}/${m}`}</div>
        <div style={{ fontSize: 14, color: th.muted, margin: "6px 0 16px" }}>
          {daily
            ? `${justificarDe.pendentes} pendência(s) da fila de ${d}/${m}. Uma única resposta para o dia; não é necessário justificar cada contato. As sobras respeitarão a cota diária.`
            : `${itens.length} contato${itens.length > 1 ? "s ficaram" : " ficou"} sem mensagem. Conte o motivo de cada um. Eles continuam na sua fila de hoje.`}
        </div>
        {itens.map(it => (
          <div key={it.idx} style={{ borderTop: `1px solid ${th.nuLine || th.sandDark}`, padding: "12px 0" }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{it.nome}</div>
            <select aria-label={daily ? "Motivo do dia" : `Motivo para ${it.nome}`} value={respostas[it.idx]?.motivo || ""} onChange={e => setRespostas(r => ({ ...r, [it.idx]: { ...r[it.idx], motivo: e.target.value } }))}
              style={{ width: "100%", marginTop: 6, padding: "8px 10px", borderRadius: 4, border: `1px solid ${th.nuLine || th.sandDark}`, fontSize: 14, fontFamily: "inherit" }}>
              <option value="">Selecione o motivo…</option>
              {(motivos || []).map(mo => <option key={mo.id} value={mo.id}>{mo.label}</option>)}
            </select>
            {respostas[it.idx]?.motivo === "outro" && (
              <input value={respostas[it.idx]?.texto || ""} onChange={e => setRespostas(r => ({ ...r, [it.idx]: { ...r[it.idx], texto: e.target.value } }))}
                placeholder="Descreva o motivo" maxLength={300}
                style={{ width: "100%", marginTop: 6, padding: "8px 10px", borderRadius: 4, border: `1px solid ${th.nuLine || th.sandDark}`, fontSize: 14, boxSizing: "border-box", fontFamily: "inherit" }} />
            )}
          </div>
        ))}
        {erro && <div style={{ color: "#B42318", fontSize: 13, margin: "8px 0" }}>{erro}</div>}
        <button type="button" disabled={!completo || salvando} onClick={salvar} style={{
          width: "100%", marginTop: 10, padding: "12px", border: "none", borderRadius: 4, background: th.accent, color: "#fff",
          fontWeight: 700, fontSize: 15, cursor: completo ? "pointer" : "not-allowed", opacity: completo ? 1 : 0.5, fontFamily: "inherit" }}>
          {salvando ? "Salvando…" : "Salvar e continuar"}
        </button>
      </div>
    </div>
  );
}

export function PlacarTombamento({ theme, placar, erro }) {
  const th = theme;
  if (erro) return <div style={{ color: "#B42318", fontSize: 13, marginBottom: 12 }}>Placar da prospecção indisponível: {erro}</div>;
  if (!placar?.linhas?.length) return null;
  const line = th.nuLine || th.sandDark;
  return (
    <section aria-label="Placar da prospecção de hoje" style={{ background: "#fff", border: `1px solid ${line}`, borderRadius: 4, padding: 14, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: th.text }}>Prospecção de hoje — cota de cada consultor</div>
      {placar.linhas.map(l => (
        <div key={`${l.storeId}-${l.consultantId}`} style={{ borderTop: `1px solid ${line}`, padding: "8px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 14 }}>
            <span style={{ fontWeight: 600 }}>{l.nome}</span>
            <span style={{ fontFamily: MONO, color: !l.abriuHoje ? th.muted : l.feitos >= l.total && l.total > 0 ? "#1E6B4A" : "#B45309" }}>
              {l.iniciado === false
                ? `começa em ${String(l.inicioTombamento || "").split("-").reverse().slice(0, 2).join("/")}`
                : l.abriuHoje ? `${l.feitos} de ${l.total}` : "ainda não abriu"}
            </span>
          </div>
          {l.justificativas?.length > 0 && (
            <div style={{ fontSize: 12, color: th.muted, marginTop: 4 }}>
              <div>Justificativas de {String(l.justificativasDe || "").split("-").reverse().slice(0, 2).join("/")}:</div>
              {l.justificativas.map((j, i) => <div key={i}>• {j.nome}: {(placar.motivos || []).find(m => m.id === j.motivo)?.label || j.motivo}{j.texto ? ` — ${j.texto}` : ""}</div>)}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}